/**
 * OEM-specific serial number validation rules.
 * Mirrors the OEM_CONFIG from the backend virtualPeaker.service.js
 */

interface OemSerialConfig {
  label: string;
  minLength: number;
  maxLength: number;
  digitsOnly: boolean;
  pattern?: RegExp;
  patternMessage?: string;
  keywords: string[];
}

const OEM_SERIAL_CONFIG: Record<string, OemSerialConfig> = {
  tesla: {
    label: "Serial Number",
    minLength: 14,
    maxLength: 16,
    digitsOnly: false,
    pattern: /^[A-Za-z0-9]+$/,
    patternMessage: "Serial Number must contain only letters and digits",
    keywords: ["tesla", "powerwall"],
  },
  enphase: {
    label: "Site ID",
    minLength: 6,
    maxLength: 7,
    digitsOnly: true,
    keywords: ["enphase"],
  },
  franklinwh: {
    label: "aGate Serial Number",
    minLength: 20,
    maxLength: 20,
    digitsOnly: true,
    keywords: ["franklin"],
  },
};

/**
 * Find the OEM config by matching batteryOem string against keywords.
 */
export const getOemConfig = (
  batteryOem: string | null | undefined
): OemSerialConfig | null => {
  if (!batteryOem) return null;
  const lower = batteryOem.toLowerCase();

  for (const config of Object.values(OEM_SERIAL_CONFIG)) {
    if (config.keywords.some((kw) => lower.includes(kw))) {
      return config;
    }
  }
  return null;
};

/**
 * Validate a serial number against the OEM rules.
 * Returns true if valid, or an error message string if invalid.
 * Note: empty/required check is handled by react-hook-form's `required` rule.
 */
export const validateSerialNumber = (
  value: string,
  batteryOem: string | null | undefined
): string | true => {
  const trimmed = value?.trim() || "";
  if (!trimmed) return true; // Let react-hook-form `required` rule handle empty values

  const config = getOemConfig(batteryOem);
  if (!config) {
    // No OEM-specific rules — only enforce general max length
    if (trimmed.length > 50) {
      return "Serial number must not exceed 50 characters";
    }
    return true;
  }

  const { label, minLength, maxLength, digitsOnly, pattern, patternMessage } = config;
  const expectedLength =
    minLength === maxLength
      ? `exactly ${minLength}`
      : `${minLength}-${maxLength}`;

  if (trimmed.length < minLength || trimmed.length > maxLength) {
    return `${label} must be ${expectedLength} characters (got ${trimmed.length})`;
  }

  if (digitsOnly && !/^\d+$/.test(trimmed)) {
    return `${label} must contain only digits`;
  }

  if (pattern && !pattern.test(trimmed)) {
    return patternMessage || `${label} format is invalid`;
  }

  return true;
};

/**
 * Get the OEM-specific label for the serial number field.
 */
export const getSerialNumberLabel = (
  batteryOem: string | null | undefined
): string => {
  const config = getOemConfig(batteryOem);
  return config ? config.label : "Serial Number";
};

/**
 * Get a hint text describing the expected format.
 */
export const getSerialNumberHint = (
  batteryOem: string | null | undefined
): string | null => {
  const config = getOemConfig(batteryOem);
  if (!config) return null;

  const { label, minLength, maxLength, digitsOnly } = config;
  const lengthText =
    minLength === maxLength ? `${minLength}` : `${minLength}-${maxLength}`;
  const typeText = digitsOnly ? "digits" : "characters";

  return `${label}: ${lengthText} ${typeText}`;
};
